/*
 * Copyright (c) 2022.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, NgForm, ValidatorFn, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';

import {Animal, AnimalCategory, AnimalDetail, AnimalSpecies, Atm, Autopsy, Country, Removal, RemovalPoint, ThirdParty} from '../../_models/models';

import {ANIMAL_NUMBER_DUPLICATE, AnimalDetailService, AnimalService, AtmService, AuthService} from '../../_services/services';
import {ExternalImportRemovalService} from '../../_services/external-import-removal.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {DialogHelper} from '../../_helpers/dialog-helper';
import {AnimalImportType, RemovalAnimalImportType} from '../../_forms/animal-import-type';
import {RemovalImportType} from '../../_forms/removal-import-type';
import {Helpers} from '../../helpers';
import {ExternalImportRemoval} from '../../_models/external-import-removal';
import {ALREADY_EXISTS, ATM_INVALID, CONFLICT, NOT_ALLOWED_SITE} from '../../_enums/requests';
import {AND, EQ} from '../../_enums/operators';
import {DEFAULT, EMBEDDED} from '../../_enums/serializer-groups';
import {HOME_PATH, SITE} from '../../_enums/constants';
import {I18nService, RP_PHONE_NEEDED} from '../../_services/i18n.service';
import {ToolsHelper} from '../../_helpers/tools-helper';
import {Unique} from '../../_validators/unique.validator';
import {AnimalNumberPatternMatch} from '../../_validators/animal-number-pattern-match.validator';
import {ExternalImportAnimal} from '../../_models/external-import-animal';


@Component({
  selector: 'app-removal',
  templateUrl: './removal.component.html'
})
export class RemovalComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VIEW CHILD                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @ViewChild('animalForm') animalForm: NgForm;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public externalRemovalId: number;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS GROUPS                                                                                          ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public animalFG: FormGroup;
  public removalFG: FormGroup;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              STATIC METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  static getAnimalValidators(animal: ExternalImportAnimal | Animal): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (animal._embedded.animalDetail && 1 === animal.quantity) {
      if (animal._embedded.animalDetail.needNumber || animal.autopsyId) {
        validators.push(Validators.required);
      }

      Validators.pattern(animal._embedded.animalDetail.numberRegex);
    }

    return validators;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGIC METHODS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    private animalService: AnimalService,
    private animalDetailService: AnimalDetailService,
    private atmService: AtmService,
    private authService: AuthService,
    public dialog: MatDialog,
    private dialogHelper: DialogHelper,
    private externalImportRemovalService: ExternalImportRemovalService,
    private formBuilder: FormBuilder,
    private i18n: I18nService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) {
  }

  ngOnInit() {
    this.externalRemovalId = this.pathId;
    this.initAnimalFG();
    this.initRemovalFG();
    this.loadExternalRemoval();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              REMOVAL FORM CONTROLS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get animalsFA(): FormArray {
    return this.removalFG.get('animals') as FormArray;
  }

  get commentsFC(): AbstractControl {
    return this.removalFG.get('comments');
  }

  get removalPointFC(): AbstractControl {
    return this.removalFG.get('removalPoint');
  }

  get thirdPartyFC(): AbstractControl {
    return this.removalFG.get('thirdParty');
  }

  get sRemovalPointFC(): AbstractControl {
    return this.removalFG.get('sRemovalPoint');
  }

  get sThirdPartyFC(): AbstractControl {
    return this.removalFG.get('sThirdParty');
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              ANIMAL FORM CONTROLS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get animalCategoryFC(): AbstractControl {
    return this.animalFG.get('animalCategory');
  }

  get animalGenderFC(): AbstractControl {
    return this.animalFG.get('animalGender');
  }

  get animalSpeciesFC(): AbstractControl {
    return this.animalFG.get('animalSpecies');
  }

  get animalTypeFC(): AbstractControl {
    return this.animalFG.get('animalType');
  }

  get autopsyFC(): AbstractControl {
    return this.animalFG.get('autopsy');
  }

  get countryFC(): AbstractControl {
    return this.animalFG.get('country');
  }

  get numberFC(): AbstractControl {
    return this.animalFG.get('number');
  }

  get quantityFC(): AbstractControl {
    return this.animalFG.get('quantity');
  }

  get weightFC(): AbstractControl {
    return this.animalFG.get('weight');
  }

  get sAnimalCategoryFC(): AbstractControl {
    return this.animalFG.get('sAnimalCategory');
  }

  get sAnimalDetailFC(): AbstractControl {
    return this.animalFG.get('sAnimalDetail');
  }

  get sAnimalSpeciesFC(): AbstractControl {
    return this.animalFG.get('sAnimalSpecies');
  }

  get sAutopsyFC(): AbstractControl {
    return this.animalFG.get('sAutopsy');
  }

  get sCountryFC(): AbstractControl {
    return this.animalFG.get('sCountry');
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get animalCategoryNeedGender(): boolean {
    return this.sAnimalCategory ? this.sAnimalCategory.needGender : false;
  }

  get animalCategoryNeedType(): boolean {
    return this.sAnimalCategory ? this.sAnimalCategory.needType : false;
  }

  get animalNumberIsRequired(): boolean {
    return this.sAnimalDetail ? this.sAnimalDetail.needNumber : false;
  }

  get animalHasAutopsy(): boolean {
    return (this.sAnimalDetail && this.sAnimalDetail.allowAutopsy && String(null) !== String(this.autopsyFC.value));
  }

  get animalDetailAtm(): Atm | null {
    return (this.sAnimalDetail && this.sAnimalDetail._embedded) ? this.sAnimalDetail._embedded.atm : null;
  }

  get canGetAnimalDetail(): boolean {
    if (this.animalCategoryNeedGender && !this.animalGenderFC.value) {
      return false;
    }

    return !(this.animalCategoryNeedType && !this.animalTypeFC.value);
  }

  get hq(): RemovalPoint | null {
    if (this.sThirdParty && this.sThirdParty._embedded && this.sThirdParty._embedded.headquarter) {
      return this.sThirdParty._embedded.headquarter;
    }

    return null;
  }

  get isTPLocked(): boolean {
    return this.sThirdParty && (this.sThirdParty.locked || !this.sThirdParty.enabled);
  }

  get onlyOneAnimal(): boolean {
    return this.quantityFC && this.quantityFC.value === 1;
  }

  get pathId(): number | null {
    if (this.route.snapshot.queryParams.id) {
      return Number(this.route.snapshot.queryParams.id);
    }

    if (this.route.snapshot.params.id) {
      return Number(this.route.snapshot.params.id);
    }

    return null;
  }

  get rOQty(): boolean {
    if ((this.sAnimalDetail && !this.sAnimalDetail.allowGrouping) || (this.numberFC.value && 0 !== this.numberFC.value.length) ||
      this.sAnimalDetail.needNumber || (this.autopsyFC && this.autopsyFC.value)) {
      this.quantityFC.setValue(1);
      return true;
    }

    return false;
  }

  get rpHasPhones(): boolean {
    return this.sRemovalPoint && (this.sRemovalPoint.phones || this.sRemovalPoint.mobilePhones) &&
      (0 < this.sRemovalPoint.phones.length || 0 < this.sRemovalPoint.mobilePhones.length);
  }

  get showAnimalGender(): boolean {
    return this.sAnimalCategory ? this.sAnimalCategory.allowGender : false;
  }

  get showAnimalNumber(): boolean {
    return this.sAnimalDetail && this.sAnimalDetail.allowNumber && this.sAnimalSpecies && false === this.sAnimalSpecies.needSplitNumber && this.onlyOneAnimal;
  }

  get showAnimalNumberSplit(): boolean {
    return this.sAnimalSpecies && this.sAnimalDetail && this.sAnimalDetail.allowNumber && this.onlyOneAnimal ? this.sAnimalSpecies.needSplitNumber : false;
  }

  get showAnimalType(): boolean {
    return this.sAnimalCategory ? this.sAnimalCategory.allowType : false;
  }

  get showAutopsy(): boolean {
    return this.sAnimalDetail && this.onlyOneAnimal ? this.sAnimalDetail.allowAutopsySite : false;
  }

  get showCountry(): boolean {
    return this.sAnimalDetail && this.onlyOneAnimal ? this.sAnimalDetail.allowNumber : false;
  }

  get showCSO(): boolean {
    return this.sThirdParty && this.sThirdParty.cso ? this.sThirdParty.cso : false;
  }

  get showQuantity(): boolean {
    return this.sAnimalDetail ? !this.sAnimalDetail.lot : false;
  }

  get showWeight(): boolean {
    return this.sAnimalDetail ? this.sAnimalDetail.lot : false;
  }

  get sAnimalCategory(): AnimalCategory | null {
    return this.sAnimalCategoryFC && this.sAnimalCategoryFC.value ? this.sAnimalCategoryFC.value : null;
  }

  get sAnimalDetail(): AnimalDetail | null {
    return this.sAnimalDetailFC && this.sAnimalDetailFC.value ? this.sAnimalDetailFC.value : null;
  }

  get sAnimalSpecies(): AnimalSpecies | null {
    return this.sAnimalSpeciesFC && this.sAnimalSpeciesFC.value ? this.sAnimalSpeciesFC.value : null;
  }

  get sAutopsy(): Autopsy | null {
    return this.sAutopsyFC && this.sAutopsyFC.value ? this.sAutopsyFC.value : null;
  }

  get sCountry(): Country | null {
    return this.sCountryFC && this.sCountryFC.value ? this.sCountryFC.value : null;
  }

  get sRemovalPoint(): RemovalPoint | null {
    return this.sRemovalPointFC && this.sRemovalPointFC.value ? this.sRemovalPointFC.value : null;
  }

  get sThirdParty(): ThirdParty | null {
    return this.sThirdPartyFC && this.sThirdPartyFC.value ? this.sThirdPartyFC.value : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              ASYNC INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private loadExternalRemoval() {
    if (this.externalRemovalId) {
      Helpers.setLoading(true);

      this.externalImportRemovalService.getById(this.externalRemovalId, {
        serializerGroups: [DEFAULT, EMBEDDED],
        version: 1.3
      }).subscribe((res: HttpResponse<ExternalImportRemoval>) => {
        Helpers.setLoading(false);
        this.updateRemovalFormData(res.body);
      }, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private initAnimalFG() {
    this.animalFG = this.formBuilder.group({
      animalSpecies: [null, Validators.required],
      animalCategory: [null, Validators.required],
      animalGender: null,
      animalType: null,
      country: [null, Validators.required],
      number: null,
      quantity: [1, [Validators.required, Validators.min(1)]],
      weight: [0, [Validators.required, Validators.min(1)]],
      autopsy: null,
      sAnimalCategory: null,
      sAnimalDetail: null,
      sAnimalSpecies: null,
      sAutopsy: null,
      sCountry: null
    });

    this.animalFG.reset();
  }

  private initRemovalFG() {
    this.removalFG = this.formBuilder.group({
      animals: this.formBuilder.array([], Validators.required),
      thirdParty: [null, Validators.required],
      removalPoint: [null, Validators.required],
      comments: [null, Validators.maxLength(120)],
      tpPhones: null,
      tpMobilePhones: null,
      rpPhones: null,
      rpMobilePhones: null,
      sRemovalPoint: null,
      sThirdParty: null
    }, {
      validator: [
        AnimalNumberPatternMatch('animals'),
        Unique('animals', 'number')
      ]
    });

    this.removalFG.reset();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                    LISTENERS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public animalCategoryChange(e?: AnimalCategory): void {
    this.sAnimalCategoryFC.setValue(ToolsHelper.hardCopy(e));
    this.resetAnimalForm(false, false);
    this.updateAnimalDetail();
  }

  public animalSpeciesChange(e?: AnimalSpecies): void {
    this.sAnimalSpeciesFC.setValue(ToolsHelper.hardCopy(e));
    this.resetAnimalForm(false, true);
  }

  public autopsyChange(e?: Autopsy): void {
    if (e) {
      this.sAutopsyFC.setValue(ToolsHelper.hardCopy(e));

      this.dialogHelper.showConfirmAutopsy(e).subscribe((res: boolean) => {
        if (!res) {
          this.autopsyFC.setValue(null);
          this.sAutopsyFC.setValue(null);
        }

        this.updateValidators();
      });
    } else {
      this.updateValidators();
    }
  }

  public countryChange(e?: Country): void {
    this.sCountryFC.setValue(ToolsHelper.hardCopy(e));
  }

  public phonesChange(e?: RemovalPoint, isHQ?: boolean): void {
    const rp = ToolsHelper.hardCopy(e);
    const tp = ToolsHelper.hardCopy(this.sThirdPartyFC.value);

    if ((isHQ && tp && tp._embedded && tp._embedded.headquarter) || (this.hq && rp && this.hq.id === rp.id)) {
      tp._embedded.headquarter = rp;
    }

    this.sThirdPartyFC.setValue(tp);
    this.sRemovalPointFC.setValue(rp);
  }

  private resetAnimalForm(resetAnimalSpecies: boolean, resetAnimalCategory: boolean): void {
    if (resetAnimalSpecies) {
      this.animalSpeciesFC.setValue(null);
      this.sAnimalSpeciesFC.setValue(null);
    }

    if (resetAnimalCategory) {
      this.animalCategoryFC.setValue(null);
      this.sAnimalCategoryFC.setValue(null);
    }

    this.animalGenderFC.setValue(null);
    this.animalTypeFC.setValue(null);
    this.autopsyFC.setValue(null);
    this.countryFC.setValue(null);
    this.numberFC.setValue(null);
    this.quantityFC.setValue(1);
    this.sAnimalDetailFC.setValue(null);
    this.sAutopsyFC.setValue(null);
    this.sCountryFC.setValue(null);
    this.weightFC.setValue(0);

    this.updateValidators();
  }

  public rpChange(e?: RemovalPoint): void {
    this.sRemovalPointFC.setValue(ToolsHelper.hardCopy(e));
    this.resetAnimalForm(true, true);
  }

  public tpChange(e?: ThirdParty): void {
    const oldTP = ToolsHelper.hardCopy(this.sThirdPartyFC.value);
    this.sThirdPartyFC.setValue(ToolsHelper.hardCopy(e));
    this.resetAnimalForm(true, true);

    if (this.isTPLocked) {
      this.tpLocked();
    } else if (oldTP && this.sThirdParty && oldTP.id !== this.sThirdParty.id) {
      this.sRemovalPointFC.setValue(null);
      this.removalPointFC.setValue(null);
    }
  }

  public updateAnimalDetail() {
    if (this.canGetAnimalDetail) {
      Helpers.setLoading(true);

      this.animalDetailService.find({
        filter: [
          ['animalCategory', EQ, this.animalCategoryFC.value], AND,
          ['animalGender', EQ, this.animalGenderFC.value], AND,
          ['animalType', EQ, this.animalTypeFC.value]
        ],
        serializerGroups: [DEFAULT, EMBEDDED],
        version: 0.6
      }).subscribe((res: HttpResponse<AnimalDetail[]>) => {
        if (1 === res.body.length) {
          this.sAnimalDetailFC.setValue(res.body[0]);
          this.weightFC.setValue(this.sAnimalDetail.weight);
        }

        Helpers.setLoading(false);
        this.updateValidators();
      }, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS SUBMITS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public animalFormSubmit() {
    if (this.animalFG.valid) {
      if (this.isAnimalNumberDuplicate()) {
        this.toastr.warning(this.i18n.get(ANIMAL_NUMBER_DUPLICATE, this.numberFC.value));
      } else {
        const animal: AnimalImportType = {
          dateOfBirth: null,
          quantity: Number(this.quantityFC.value),
          atm: this.animalDetailAtm ? Number(this.animalDetailAtm.id) : null,
          weightE: Number(this.weightFC.value),
          autopsy: this.autopsyFC && this.autopsyFC.value && this.onlyOneAnimal ? Number(this.autopsyFC.value) : null,
          country: this.countryFC && this.countryFC.value && this.onlyOneAnimal ? Number(this.countryFC.value) : null,
          animalDetail: Number(this.sAnimalDetail.id),
          number: this.numberFC.value && this.onlyOneAnimal ? String(this.numberFC.value) : ''
        };

        if (this.animalDetailAtm.certificate) {
          Helpers.setLoading(true);

          this.atmService.getDefault().subscribe((res: Atm) => {
            Helpers.setLoading(false);

            this.dialogHelper.showAngee().subscribe((resDialog: boolean) => {
              if (!resDialog) {
                animal.atm = Number(res.id);
              }

              this.checkAnimal(this.removalPointFC.value, animal);
            });
          }, (err: HttpErrorResponse) => this.handleError(err));
        } else {
          this.checkAnimal(this.removalPointFC.value, animal);
        }
      }
    }
  }

  public removalFormSubmit() {
    if (this.isTPLocked) {
      this.tpLocked();
    } else {
      if (!this.rpHasPhones) {
        this.toastr.warning(this.i18n.get(RP_PHONE_NEEDED));
      } else if (this.removalFG.valid) {
        const removal: RemovalImportType = {
          callBeforeTime: null,
          comments: this.commentsFC.value,
          office: SITE,
          thirdParty: this.thirdPartyFC.value,
          removalPoint: this.removalPointFC.value,
          animals: this.animalsFA.value
        };

        Helpers.setLoading(true);

        this.externalImportRemovalService.create(removal, {
          serializerGroups: [DEFAULT, EMBEDDED],
          version: 0.3
        }).subscribe(
          (res: HttpResponse<ExternalImportRemoval>) => this.externalRemovalResponseHandle(res.body),
          (err: HttpErrorResponse) => this.handleError(err));
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              CONFIRMS                                                                                              ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private checkAnimal(removalPointId: number, animal: AnimalImportType): void {
    Helpers.setLoading(true);

    this.animalService.checkAnimal(removalPointId, animal, {
      serializerGroups: [DEFAULT, EMBEDDED],
      version: 0.6
    }).subscribe((res: HttpResponse<Animal>) => {
      const validAnimal = {
        dateOfBirth: res.body.dateOfBirth,
        quantity: res.body.quantity,
        atm: res.body.atmId,
        weightE: res.body.weightE,
        autopsy: res.body.autopsyId,
        country: res.body.countryId,
        animalDetail: res.body.animalDetailId,
        number: [res.body.number, Validators.compose(RemovalComponent.getAnimalValidators(res.body))],
        _embedded: {
          animalDetail: res.body._embedded.animalDetail,
          atm: res.body._embedded.atm,
          autopsy: res.body._embedded.autopsy,
          country: res.body._embedded.country
        }
      };

      this.animalsFA.push(this.formBuilder.group(validAnimal));
      this.initAnimalFG();

      if (this.animalForm) {
        this.animalForm.resetForm();
      }

      this.resetAnimalForm(true, true);
      Helpers.setLoading(false);
    }, (err: HttpErrorResponse) => {
      if (CONFLICT === err.status && err.error && err.error.code && err.error.message) {
        Helpers.setLoading(false);

        switch (err.error.code) {
          case ATM_INVALID:
            this.dialogHelper.showAtmInvalid(err.error.message);
            break;
          case NOT_ALLOWED_SITE:
            this.dialogHelper.showNotAllowedSite(err.error.message);
            break;
          case ALREADY_EXISTS:
          default:
            this.toastr.warning(err.error.message);
        }
      } else {
        this.handleError(err);
      }
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALIDATION                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private isAnimalNumberDuplicate(): boolean {
    if ((this.numberFC && !this.numberFC.value) || ('' === this.numberFC.value.toString().trim()) || (this.animalsFA && !this.animalsFA.value)) {
      return false;
    }

    return 0 < this.animalsFA.value.map((it: RemovalAnimalImportType) => it.number).filter((it: string) => this.numberFC.value === it).length;
  }

  private updateAnimalGenderValidator(): void {
    this.animalGenderFC.setValidators(null);

    if (this.sAnimalCategory && this.sAnimalCategory.allowGender && this.animalCategoryNeedGender) {
      this.animalGenderFC.setValidators([Validators.required]);
    }

    this.animalGenderFC.updateValueAndValidity();
  }

  private updateAnimalNumberValidators(): void {
    this.numberFC.setValidators(null);

    if (this.showAnimalNumber || this.showAnimalNumberSplit) {
      const validators = [Validators.pattern(this.sAnimalDetail.numberRegex)];

      if (this.animalNumberIsRequired || this.animalHasAutopsy) {
        validators.push(Validators.required);
      }

      this.numberFC.setValidators(validators);
    }

    this.numberFC.updateValueAndValidity();
  }

  private updateCountryValidator(): void {
    this.countryFC.setValidators(null);

    if ((this.sAnimalDetail && this.sAnimalDetail.allowNumber) || this.animalNumberIsRequired) {
      this.countryFC.setValidators([Validators.required]);
    }

    this.countryFC.updateValueAndValidity();
  }


  private updateAnimalTypeValidator(): void {
    this.animalTypeFC.setValidators(null);

    if (this.sAnimalCategory && this.sAnimalCategory.allowType && this.animalCategoryNeedType) {
      this.animalTypeFC.setValidators([Validators.required]);
    }

    this.animalTypeFC.updateValueAndValidity();
  }

  private updateQuantityValidator(): void {
    this.quantityFC.setValidators(null);

    if (this.autopsyFC.value) {
      this.quantityFC.setValidators([Validators.max(1), Validators.min(1)]);
    }

    this.quantityFC.updateValueAndValidity();
  }

  public updateValidators(): void {
    this.updateAnimalGenderValidator();
    this.updateAnimalNumberValidators();
    this.updateAnimalTypeValidator();
    this.updateCountryValidator();
    this.updateQuantityValidator();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private externalRemovalResponseHandle(externalRemoval: ExternalImportRemoval): void {
    Helpers.setLoading(false);

    this.dialogHelper.showRemovalValidate(this.removalFG.value, externalRemoval).subscribe((e?: Removal) => {
      if (e) {
        this.dialogHelper.showRemovalFinished(e).subscribe(() => {
          this.router.navigate([HOME_PATH]);
        });
      }
    });
  }

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

  private tpLocked(): void {
    this.dialogHelper.showThirdPartyLocked().subscribe(() => {
      this.router.navigate([HOME_PATH]);
    });
  }

  private updateRemovalFormData(externalRemoval: ExternalImportRemoval) {
    if (externalRemoval) {
      externalRemoval.animals.forEach((it) => {
        const validAnimal = {
          dateOfBirth: it.dateOfBirth,
          quantity: it.quantity,
          atm: it.atmId,
          weightE: it.weightE,
          autopsy: it.autopsyId,
          country: it.countryId,
          animalDetail: it.animalDetailId,
          number: [it.number, Validators.compose(RemovalComponent.getAnimalValidators(it))],
          _embedded: {
            animalDetail: it._embedded.animalDetail,
            atm: it._embedded.atm,
            autopsy: it._embedded.autopsy,
            country: it._embedded.country
          }
        };

        this.animalsFA.push(this.formBuilder.group(validAnimal));
      });

      this.commentsFC.setValue(externalRemoval.comments);
      this.thirdPartyFC.setValue(externalRemoval.thirdPartyId);
      this.removalPointFC.setValue(externalRemoval.removalPointId);
    }
  }

}
